import { useCallback, useEffect, useState } from 'react';
import axios, { AxiosRequestConfig } from 'axios';
import Bugsnag from "@bugsnag/js";

import store from '_store';
import { logOut } from '_store/system/actions';
import { issueNotification } from '_store/notifications/actions';
import { API_URI } from '_utils/config';

type IdleState = {kind: "idle";}
let idleState = () => ({kind: "idle"}) as IdleState;

type RequestedState = {kind: "requested"; request: RequestInfo;}
let requestedState = (request: RequestInfo) => ({kind: "requested", request}) as RequestedState;

type LoadingState = {kind: "loading";}
let loadingState = () => ({kind: "loading"}) as LoadingState;

type SuccessState<P> = {kind: "success"; payload: P;}
let successState = <P = {}>(payload: P) => ({kind: "success", payload}) as SuccessState<P>;

type NetworkError = {kind: "network";}
type MaintenanceError = {kind: "maintenance";}
type CustomError<E> = {kind: "custom"; type: E;}
type UnknownError = {kind: "unknown";}
let network = () => ({kind: "network"}) as NetworkError;
let maintenance = () => ({kind: "maintenance"}) as MaintenanceError;
let custom = <E>(type: E) => ({kind: "custom", type}) as CustomError<E>;
let unknown = () => ({kind: "unknown"}) as UnknownError;

type ErrorType<E> = NetworkError | MaintenanceError | CustomError<E> | UnknownError;
type ErrorState<E> = {kind: "error"; error: ErrorType<E>;}
let errorState = <E = null>(error: ErrorType<E>) => ({kind: "error", error}) as ErrorState<E>;

type RequestState<P, E> = IdleState | RequestedState | LoadingState | SuccessState<P> | ErrorState<E>;

// Specialised type guards - checks are more restrictive than signatures suggest.
const isCustomError = <E>(error: ErrorType<E>, type: E): error is CustomError<E> =>
  (error.kind === "custom") && (error.type === type);

export const isCustomErrorState = <P, E>(response: RequestState<P, E>, type: E): response is ErrorState<E> =>
  (response.kind === "error") && isCustomError(response.error, type);

// Request factory
type method = 'GET' | 'POST' | 'PUT';
export const makeRequest = (endpoint: string, method: method = 'GET', body?: object | [any]): Request => {
  // check whether the user is logged in
  const user = store.getState().system.user;

  const headers = new Headers();
  // only advertise JSON content when body is not empty
  if (body !== undefined) { headers.append('Content-Type', 'application/json'); }
  if (user != null) { headers.append('X-Auth-Token', user.authToken); }

  return new Request(`${API_URI}${endpoint}`, {
    method,
    headers,
    body: body && JSON.stringify(body),
  });
}

// Request hook - if request is provided, it's executed immediately. It's also executed upon calling fetchResult.
export const useRequest = <P = void, E = {}>(request?: RequestInfo, getError?: (response: Response) => E | null) => {
  const [state, setState] = useState<RequestState<P, E>>((request != null) ? requestedState(request) : idleState());

  const handleResponseError = useCallback((response: Response) => {
    if (response.status === 502) { setState(errorState(maintenance())); return }
    // TODO: add {metaData: {response}})
    Bugsnag.notify(new Error(JSON.stringify(response)))
    setState(errorState(unknown()))
    if (response.status === 401) {
      store.dispatch(issueNotification('You have been signed out.', 'warning'))
      // TODO: change store's type to support redux-thunk and use endSession
      store.dispatch(logOut())
    }
  }, [setState])

  useEffect(() => {
    // only perform fetch when state becomes the loading state
    if (state.kind !== "requested") return;
    const request = state.request;
    setState(loadingState());
    const t = async () => {
      try {
        const response = await fetch(request);
        if (response.ok) {
          let json =  await response.text();
          let payload: P = json && JSON.parse(json);
          setState(successState(payload));
        }
        else {
          if (getError) {
            const error = getError(response);
            if (error != null) { setState(errorState(custom<E>(error))) }
            else { handleResponseError(response) }
          } else { handleResponseError(response) }
        }
      }
      catch (e) {
        // If fetch fails due to a network error, it throws a TypeError.
        if (e instanceof TypeError) {
          setState(errorState(network()));
        } else {
          console.error(e);
          Bugsnag.notify(e);
          setState(errorState(unknown()));
        }
      }
    }
    t();
  }, [getError, handleResponseError, state]);

  const fetchResult = (request: RequestInfo) => setState(requestedState(request));
  return [state, fetchResult] as [RequestState<P, E>, (request: RequestInfo) => void];
};

export const errorMessage: <E>(error: ErrorType<E>, customMessages?: Map<E, string>) => string = (error, customMessages) => {
  switch (error.kind) {
    case "network":
      return "No network connection."
    case "maintenance":
      return "Our systems are currently undergoing maintenance. Please come back later."
    case "custom":
      return (customMessages && customMessages.get(error.type)) || "An error has occurred.";
    case "unknown":
      return "An error has occurred. We're looking into it.";
  }
}

export const requestFileUpload = async <P>(endpoint: string, file: File, config: AxiosRequestConfig) => {
  const user = store.getState().system.user;
  const authHeader = (user === null) ? {} : {"X-Auth-Token": user.authToken};
  const headers = {...authHeader, "Content-Type": "multipart/form-data"}

  const formData = new FormData();
  formData.append("file", file);

  const res = await axios.post<P>(
    `${API_URI}${endpoint}`,
    formData,
    {...config, headers});
  return res.data;
}
